/**
 * Camp geometry & seat layouts — single source of truth.
 * Axial hex (pointy-top); coordinates follow the authoritative 121-hole
 * hexagram enumeration in docs/volumes/04-geometry/03-cell-enumeration.md §7.2.
 * Core hexagon = radius 4 (61 holes); each star arm = an edge-5 triangle of
 * exactly 10 holes whose apex sits at distance 8 from the centre.
 * Camp k = star corner: 0=NE, 1=E, 2=SE, 3=SW, 4=W, 5=NW;
 * the 60° rotation operator R(q,r)=(-r, q+r) maps corner 0→1→…→5→0.
 * destination = camp (i + 3) % 6 (opposite star corners).
 */
import type { HexPos } from '../types/game.js';
import { isOnBoard, hexKey } from '../utils/hexMath.js';

/** 10 hole positions for each camp, ordered tip → base (apex first). */
export const CAMP_POSITIONS: Readonly<Record<number, readonly HexPos[]>> = {
  0: [
    // corner 0 · NE · apex (4,-8)
    { q: 4, r: -8 },
    { q: 3, r: -7 },
    { q: 4, r: -7 },
    { q: 2, r: -6 },
    { q: 3, r: -6 },
    { q: 4, r: -6 },
    { q: 1, r: -5 },
    { q: 2, r: -5 },
    { q: 3, r: -5 },
    { q: 4, r: -5 },
  ],
  1: [
    // corner 1 · E · apex (8,-4)
    { q: 8, r: -4 },
    { q: 7, r: -4 },
    { q: 7, r: -3 },
    { q: 6, r: -4 },
    { q: 6, r: -3 },
    { q: 6, r: -2 },
    { q: 5, r: -4 },
    { q: 5, r: -3 },
    { q: 5, r: -2 },
    { q: 5, r: -1 },
  ],
  2: [
    // corner 2 · SE · apex (4,4)
    { q: 4, r: 4 },
    { q: 4, r: 3 },
    { q: 3, r: 4 },
    { q: 4, r: 2 },
    { q: 3, r: 3 },
    { q: 2, r: 4 },
    { q: 4, r: 1 },
    { q: 3, r: 2 },
    { q: 2, r: 3 },
    { q: 1, r: 4 },
  ],
  3: [
    // corner 3 · SW · apex (-4,8)
    { q: -4, r: 8 },
    { q: -3, r: 7 },
    { q: -4, r: 7 },
    { q: -2, r: 6 },
    { q: -3, r: 6 },
    { q: -4, r: 6 },
    { q: -1, r: 5 },
    { q: -2, r: 5 },
    { q: -3, r: 5 },
    { q: -4, r: 5 },
  ],
  4: [
    // corner 4 · W · apex (-8,4)
    { q: -8, r: 4 },
    { q: -7, r: 4 },
    { q: -7, r: 3 },
    { q: -6, r: 4 },
    { q: -6, r: 3 },
    { q: -6, r: 2 },
    { q: -5, r: 4 },
    { q: -5, r: 3 },
    { q: -5, r: 2 },
    { q: -5, r: 1 },
  ],
  5: [
    // corner 5 · NW · apex (-4,-4)
    { q: -4, r: -4 },
    { q: -4, r: -3 },
    { q: -3, r: -4 },
    { q: -4, r: -2 },
    { q: -3, r: -3 },
    { q: -2, r: -4 },
    { q: -4, r: -1 },
    { q: -3, r: -2 },
    { q: -2, r: -3 },
    { q: -1, r: -4 },
  ],
} as const;

/** Seat layouts & clockwise turn orders per player count (RULES.md §6). */
export const SEAT_LAYOUTS: Readonly<Record<number, readonly number[]>> = {
  2: [0, 3],
  3: [0, 2, 5],
  4: [0, 2, 3, 5],
  6: [0, 1, 2, 3, 4, 5],
} as const;

export const SUPPORTED_PLAYER_COUNTS = [2, 3, 4, 6] as const;
export type PlayerCount = (typeof SUPPORTED_PLAYER_COUNTS)[number];

/** Destination camp of a given camp. */
export function destinationCamp(camp: number): number {
  return (camp + 3) % 6;
}

const CAMP_LOOKUP: Map<string, number> = (() => {
  const m = new Map<string, number>();
  for (let camp = 0; camp < 6; camp++) {
    for (const p of CAMP_POSITIONS[camp] ?? []) m.set(hexKey(p), camp);
  }
  return m;
})();

/** Which camp (0–5) a board position belongs to, or -1 for the shared center. */
export function campOf(pos: HexPos): number {
  return CAMP_LOOKUP.get(hexKey(pos)) ?? -1;
}

/**
 * Team formats (RULES.md §11, v4.0): 4 players = 2v2, 6 players = 3v3.
 * Teams are derived from the *turn-order index* parity within the seat
 * layout (never from raw camp ids), so this module stays the single
 * source of truth: 2v2 layout [0,2,3,5] → A = indices {0,2} (camps 0,3),
 * B = indices {1,3} (camps 2,5); 3v3 → A = even indices, B = odd.
 * 2-player and 3-player games are free-for-all and have no teams.
 */
export type TeamId = 'A' | 'B';

/** Team of a seat by its turn-order index, or null for free-for-all formats. */
export function teamOfSeatIndex(playerCount: number, seatIndex: number): TeamId | null {
  if (playerCount !== 4 && playerCount !== 6) return null;
  return seatIndex % 2 === 0 ? 'A' : 'B';
}

/** Turn-order indices of all seats belonging to a team (empty for FFA formats). */
export function teamSeatIndices(playerCount: number, team: TeamId): number[] {
  if (playerCount !== 4 && playerCount !== 6) return [];
  const out: number[] = [];
  for (let i = 0; i < playerCount; i++) {
    if (teamOfSeatIndex(playerCount, i) === team) out.push(i);
  }
  return out;
}

/** All team ids participating in a format (empty for FFA). */
export function teamsOfFormat(playerCount: number): readonly TeamId[] {
  return playerCount === 4 || playerCount === 6 ? (['A', 'B'] as const) : [];
}

/** All on-board positions (121-hole hexagram) grouped by camp (-1 = shared center). */
export function positionsByCamp(): Map<number, HexPos[]> {
  const m = new Map<number, HexPos[]>();
  for (let q = -8; q <= 8; q++) {
    for (let r = -8; r <= 8; r++) {
      const pos: HexPos = { q, r };
      if (!isOnBoard(pos)) continue;
      const camp = campOf(pos);
      const list = m.get(camp) ?? [];
      list.push(pos);
      m.set(camp, list);
    }
  }
  return m;
}
